import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MessageSquare, Users, CheckCircle, XCircle, TrendingUp, Clock, Send, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, color, subValue, subLabel }) => (
  <Card className="card-hover bg-white border-0 shadow-sm" data-testid={`stat-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-[#6B7B8C]">{title}</p>
          <p className="text-3xl font-bold text-[#2C3E50] mt-1">{value}</p>
          {subValue !== undefined && (
            <p className="text-sm text-[#6B7B8C] mt-1">
              {subLabel}: <span className="font-medium text-[#2C3E50]">{subValue}</span>
            </p>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon className="h-6 w-6 text-white" strokeWidth={1.5} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentMessages, setRecentMessages] = useState([]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, messagesRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/messages?limit=5`)
      ]);
      setStats(statsRes.data);
      setRecentMessages(messagesRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      queued: 'bg-gray-100 text-gray-600',
      sent: 'bg-blue-100 text-blue-600',
      delivered: 'bg-green-100 text-green-600',
      read: 'bg-emerald-100 text-emerald-600',
      failed: 'bg-red-100 text-red-600'
    };
    const labels = {
      queued: 'En cola',
      sent: 'Enviado',
      delivered: 'Entregado',
      read: 'Leído',
      failed: 'Fallido'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.queued}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0066CC]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#2C3E50]">Dashboard</h1>
          <p className="text-[#6B7B8C] mt-1">Bienvenido, {user?.name}</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-[#6B7B8C]">
          <Clock className="h-4 w-4" />
          <span>Última actualización: {new Date().toLocaleTimeString('es-AR')}</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Mensajes"
          value={stats?.total_messages || 0}
          icon={MessageSquare}
          color="bg-[#0066CC]"
          subValue={stats?.messages_today || 0}
          subLabel="Hoy"
        />
        <StatCard
          title="Mensajes Enviados"
          value={stats?.messages_sent || 0}
          icon={Send}
          color="bg-[#00A5E0]"
          subValue={stats?.messages_this_week || 0}
          subLabel="Esta semana"
        />
        <StatCard
          title="Entregados"
          value={stats?.messages_delivered || 0}
          icon={CheckCircle}
          color="bg-[#25D366]"
        />
        <StatCard
          title="Fallidos"
          value={stats?.messages_failed || 0}
          icon={XCircle}
          color="bg-[#EF4444]"
        />
      </div>

      {/* Second Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Clients Card */}
        <Card className="bg-white border-0 shadow-sm" data-testid="clients-summary-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-[#2C3E50] flex items-center">
              <Users className="h-5 w-5 mr-2 text-[#0066CC]" />
              Clientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-4xl font-bold text-[#2C3E50]">{stats?.active_clients || 0}</p>
                <p className="text-sm text-[#6B7B8C]">Activos</p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-semibold text-[#6B7B8C]">{stats?.total_clients || 0}</p>
                <p className="text-sm text-[#6B7B8C]">Total</p>
              </div>
            </div>
            <div className="mt-4 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#25D366] rounded-full transition-all duration-500"
                style={{ width: `${stats?.total_clients ? (stats.active_clients / stats.total_clients) * 100 : 0}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Recent Messages */}
        <Card className="lg:col-span-2 bg-white border-0 shadow-sm" data-testid="recent-messages-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-semibold text-[#2C3E50] flex items-center">
              <TrendingUp className="h-5 w-5 mr-2 text-[#0066CC]" />
              Mensajes Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentMessages.length === 0 ? (
              <div className="text-center py-8 text-[#6B7B8C]">
                <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No hay mensajes recientes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentMessages.map((msg) => (
                  <div 
                    key={msg.id} 
                    className="flex items-center justify-between p-3 bg-[#F0F2F5] rounded-lg"
                    data-testid={`recent-message-${msg.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#2C3E50] truncate">{msg.to}</p>
                      <p className="text-xs text-[#6B7B8C] truncate">{msg.message}</p>
                    </div>
                    <div className="ml-4 flex items-center space-x-3">
                      {getStatusBadge(msg.status)}
                      <span className="text-xs text-[#6B7B8C]">
                        {new Date(msg.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* API Status */}
      <Card className="bg-white border-0 shadow-sm" data-testid="api-status-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-[#2C3E50] flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2 text-[#F59E0B]" />
            Estado del Sistema
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse"></div>
              <div>
                <p className="text-sm font-medium text-green-700">API</p>
                <p className="text-xs text-green-600">Operativo</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse"></div>
              <div>
                <p className="text-sm font-medium text-green-700">WhatsApp</p>
                <p className="text-xs text-green-600">Conectado (Twilio)</p>
              </div>
            </div>
            <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
              <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse"></div>
              <div>
                <p className="text-sm font-medium text-green-700">Base de Datos</p>
                <p className="text-xs text-green-600">MongoDB Conectado</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardPage;
